import api from '@/shared/utils/apiConfig';
import type { ApiResponse } from '@/shared/types/api';
import type {
  CreateLabPartnerPayload,
  LabPartner,
  LabPartnerStatus,
  UpdateLabPartnerPayload,
} from '../types/labo';

export interface LabPartnerListParams {
  status?: LabPartnerStatus;
  search?: string;
  page?: number;
  limit?: number;
}

export interface LabPartnerStats {
  orderCount: number;
  overdueCount: number;
  totalLabCost: number;
  totalExpensed: number;
}

export interface LabPartnerListResult {
  items: LabPartner[];
  total: number;
}

export async function listLabPartnersResult(
  params: LabPartnerListParams = {}
): Promise<LabPartnerListResult> {
  const response = await api.get<ApiResponse<LabPartner[]>>('/labo/partners', { params });
  const items = response.data.data || [];
  return {
    items,
    total: response.data.total ?? items.length,
  };
}

export async function listLabPartners(
  params: LabPartnerListParams = {}
): Promise<LabPartner[]> {
  const result = await listLabPartnersResult(params);
  return result.items;
}

export async function getLabPartner(id: string): Promise<LabPartner> {
  const response = await api.get<ApiResponse<LabPartner>>(`/labo/partners/${id}`);
  if (!response.data.data) {
    throw new Error('Lab partner not found');
  }
  return response.data.data;
}

export async function getLabPartnerStats(id: string): Promise<LabPartnerStats> {
  const response = await api.get<ApiResponse<LabPartnerStats>>(`/labo/partners/${id}/stats`);
  if (!response.data.data) {
    throw new Error('Lab partner stats not found');
  }
  return response.data.data;
}

export async function createLabPartner(
  payload: CreateLabPartnerPayload
): Promise<LabPartner> {
  const response = await api.post<ApiResponse<LabPartner>>('/labo/partners', payload);
  if (!response.data.data) {
    throw new Error('Failed to create lab partner');
  }
  return response.data.data;
}

export async function updateLabPartner(
  id: string,
  payload: UpdateLabPartnerPayload
): Promise<LabPartner> {
  const response = await api.put<ApiResponse<LabPartner>>(`/labo/partners/${id}`, payload);
  if (!response.data.data) {
    throw new Error('Failed to update lab partner');
  }
  return response.data.data;
}

export async function deactivateLabPartner(id: string): Promise<LabPartner> {
  const response = await api.delete<ApiResponse<LabPartner>>(`/labo/partners/${id}`);
  if (!response.data.data) {
    throw new Error('Failed to deactivate lab partner');
  }
  return response.data.data;
}
